'use es6';

import { Fragment, useState, useRef, useEffect, forwardRef } from 'react';
import autolinker from 'autolinker';
import { getLocalId } from 'conversations-internal-schema/file-upload/operators/fileUploadGetters';
import { isUploadCompleted } from 'conversations-internal-schema/file-upload/operators/isUploadCompleted';
import { DEFAULT_MAX_MESSAGE_LENGTH } from '../constants/defaultMaxMessageLength';
import I18n from 'I18n';
import { List } from 'immutable';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Raven from 'raven-js';
import MessageComposer from 'conversations-visitor-experience-components/presentation-components/MessageComposer';
import FileUpload from '../../attachments/components/FileUpload';
import TextInput from './TextInput';
import AttachmentPickerButton from '../../attachments/components/AttachmentPickerButton';
import { ALLOWED_FILE_TYPES } from 'conversations-internal-schema/file-metadata/constants/allowedFileTypes';
import VizExIconButton from 'visitor-ui-component-library/button/VizExIconButton';
import SVGSend from 'visitor-ui-component-library-icons/icons/SVGSend';
import VizExIcon from 'visitor-ui-component-library/icon/VizExIcon';
import { escape } from 'conversations-message-history/util/escape';
import { isMobileDevice } from 'conversations-visitor-experience-components/visitor-widget/util/isMobileDevice';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const FileUploadWrapper = styled.div.withConfig({
  displayName: "MessageEditor__FileUploadWrapper",
  componentId: "sc-142mntr-0"
})(["display:flex;overflow:auto;& > *:not(:last-child){margin-right:8px;}"]);
let HAS_RAVEN_FIRED = false;
const MessageEditor = /*#__PURE__*/forwardRef(({
  canUploadAttachments,
  chatInputChanged,
  coloring: {
    accentColor,
    useDefaultColor
  } = {},
  disablePublish,
  disabled,
  fileTypes,
  inputStaging,
  maxAttachmentFileSize,
  mobile,
  networkOffline,
  onAttachmentButtonClick,
  onRemoveAttachment,
  onSelectAttachmentFiles,
  publishMessage,
  shouldDisableInitialInputFocus,
  shouldHideAttachmentButton,
  shouldSendInputStagingEvent,
  showQuickReplyPlaceholder,
  showingHsBranding,
  stagedAttachments,
  stagedMessageEditorText,
  supportsAttachments,
  toggleOnAndOffInputFocus
}, ref) => {
  const [isComposing, setIsComposing] = useState(false);
  const [messageText, setMessageText] = useState(stagedMessageEditorText || '');
  const _ref = useRef();
  const editorRef = ref || _ref;
  const focusEditor = () => {
    if (editorRef !== null && editorRef !== void 0 && editorRef.current) {
      editorRef.current.focus();
    }
  };
  useEffect(() => {
    // this does the same as focusEditor, not using function so that we can have exhaustive deps
    if (!disabled && !(shouldDisableInitialInputFocus || isMobileDevice()) && editorRef !== null && editorRef !== void 0 && editorRef.current) {
      editorRef.current.focus();
    }
  }, [editorRef, disabled, shouldDisableInitialInputFocus]);
  const getMessageText = () => {
    return messageText;
  };
  const messageTextIsWithinLimit = () => {
    return getMessageText().length < DEFAULT_MAX_MESSAGE_LENGTH;
  };
  const hasAttachments = () => {
    return stagedAttachments && stagedAttachments.size;
  };
  const attachmentsAreValid = () => {
    if (!stagedAttachments) {
      return true;
    }
    return stagedAttachments.every(isUploadCompleted);
  };
  const messageContentsAreValid = () => {
    const hasTextContent = Boolean(getMessageText()) && getMessageText().trim() !== '';
    return messageTextIsWithinLimit() && attachmentsAreValid() && (hasTextContent || hasAttachments());
  };
  const canPublish = () => {
    return messageContentsAreValid() && !networkOffline && !disabled && !disablePublish;
  };
  const handleChange = e => {
    const currentMessageText = e.target.value;
    setMessageText(currentMessageText);
    if (shouldSendInputStagingEvent && !currentMessageText) {
      inputStaging(currentMessageText, true);
    }
    chatInputChanged(currentMessageText);

    // https://issues.hubspotcentral.com/browse/CT-6256
    if ((disabled || disablePublish) && !HAS_RAVEN_FIRED) {
      Raven.captureMessage('VISITOR_CAN_TYPE_WHILE_PUBLISH_DISABLED');
      HAS_RAVEN_FIRED = true;
    }
  };
  const handlePublish = () => {
    if (canPublish()) {
      const _messageText = getMessageText();
      const richText = `<div>${autolinker.link(escape(_messageText), {
        stripPrefix: false
      })}</div>`.replace(/\n/g, '<br>');
      publishMessage({
        text: _messageText,
        richText
      });
      setMessageText('');
      inputStaging('', true);
      focusEditor();
    }
  };

  /**
   * @param {KeyboardEvent} e
   */
  const handleKeyDown = e => {
    if (e.key === 'Enter' && e.shiftKey === false && !isComposing) {
      handlePublish();
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
    return true;
  };
  const getValidationMessage = () => {
    return !messageTextIsWithinLimit() ? I18n.text('conversations-visitor-ui.chatTextArea.validationMessage') : '';
  };
  const handleSelectAttachmentFiles = ({
    files
  }) => {
    onSelectAttachmentFiles({
      files
    });
    focusEditor();
  };
  const onInputFocus = () => {
    if (!disabled) toggleOnAndOffInputFocus(true);
  };
  const onInputBlur = () => {
    toggleOnAndOffInputFocus(false);
  };
  const handlePaste = e => {
    var _e$clipboardData;
    if (e !== null && e !== void 0 && (_e$clipboardData = e.clipboardData) !== null && _e$clipboardData !== void 0 && _e$clipboardData.files && canUploadAttachments) {
      onSelectAttachmentFiles({
        files: e.clipboardData.files
      });
    }
  };
  const TextInputComponent = /*#__PURE__*/_jsx(TextInput, {
    disableInitialInputFocus: shouldDisableInitialInputFocus,
    disabled: disabled || networkOffline || disablePublish,
    error: !messageTextIsWithinLimit(),
    messageText: messageText,
    onChange: handleChange,
    onKeyDown: handleKeyDown,
    showQuickReplyPlaceholder: showQuickReplyPlaceholder,
    ref: editorRef,
    showingHsBranding: showingHsBranding,
    validationMessage: getValidationMessage(),
    onInputFocus: onInputFocus,
    onInputBlur: onInputBlur,
    onPaste: handlePaste,
    onCompositionStart: () => setIsComposing(true),
    onCompositionEnd: () => setIsComposing(false)
  });
  const FileUploads = hasAttachments() ? /*#__PURE__*/_jsx(FileUploadWrapper, {
    className: 'm-bottom-2' + (showingHsBranding ? " m-top-2" : ""),
    children: stagedAttachments.map(attachment => {
      return /*#__PURE__*/_jsx("div", {
        "data-test-id": "MessageEditor-FileUploadItem",
        children: /*#__PURE__*/_jsx(FileUpload, {
          attachment: attachment,
          onRemove: () => {
            onRemoveAttachment({
              attachment
            });
          }
        })
      }, getLocalId(attachment));
    })
  }) : null;
  const color = !useDefaultColor ? accentColor : null;
  const isDisabled = !canPublish();
  return /*#__PURE__*/_jsx(MessageComposer, {
    inputToRender: TextInputComponent,
    FileUploads: FileUploads,
    showingHsBranding: showingHsBranding,
    children: /*#__PURE__*/_jsxs(Fragment, {
      children: [supportsAttachments && !shouldHideAttachmentButton ? /*#__PURE__*/_jsx("div", {
        className: mobile ? 'm-right-3' : "",
        children: /*#__PURE__*/_jsx(AttachmentPickerButton, {
          accept: fileTypes,
          color: color,
          disabled: !canUploadAttachments || disabled,
          mobile: mobile,
          onClick: onAttachmentButtonClick,
          onSelectFiles: handleSelectAttachmentFiles,
          maxAttachmentFileSize: maxAttachmentFileSize
        })
      }) : null, /*#__PURE__*/_jsx(VizExIconButton, {
        className: "chat-send-button",
        "data-test-id": "chat-send-button",
        disabled: isDisabled,
        shape: "circle",
        onClick: handlePublish,
        use: "transparent-on-background",
        "aria-label": I18n.text('conversations-visitor-ui.visitorExperienceAriaLabels.send'),
        children: /*#__PURE__*/_jsx(VizExIcon, {
          icon: /*#__PURE__*/_jsx(SVGSend, {})
        })
      })]
    })
  });
});
MessageEditor.propTypes = {
  canUploadAttachments: PropTypes.bool.isRequired,
  chatInputChanged: PropTypes.func.isRequired,
  coloring: PropTypes.object,
  disablePublish: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  fileTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  inputStaging: PropTypes.func.isRequired,
  maxAttachmentFileSize: PropTypes.number.isRequired,
  mobile: PropTypes.bool,
  networkOffline: PropTypes.bool.isRequired,
  onAttachmentButtonClick: PropTypes.func,
  onRemoveAttachment: PropTypes.func.isRequired,
  onSelectAttachmentFiles: PropTypes.func.isRequired,
  publishMessage: PropTypes.func.isRequired,
  shouldDisableInitialInputFocus: PropTypes.bool,
  shouldHideAttachmentButton: PropTypes.bool,
  shouldSendInputStagingEvent: PropTypes.bool.isRequired,
  showQuickReplyPlaceholder: PropTypes.bool.isRequired,
  showingHsBranding: PropTypes.bool,
  stagedAttachments: PropTypes.instanceOf(List),
  stagedMessageEditorText: PropTypes.string,
  supportsAttachments: PropTypes.bool.isRequired,
  toggleOnAndOffInputFocus: PropTypes.func
};
MessageEditor.displayName = 'MessageEditor';
MessageEditor.defaultProps = {
  isThreadStarted: true,
  disablePublish: false,
  onRemoveAttachment: () => {},
  onSelectAttachmentFiles: () => {},
  createNewThread: () => {},
  supportsAttachments: false,
  fileTypes: ALLOWED_FILE_TYPES,
  stagedAttachments: [],
  toggleOnAndOffInputFocus: () => {}
};
export default MessageEditor;